import {
  CreateBulkOrderRequest,
  CreateBulkOrderResponseDataInner,
  OrderBasisNextDailyInfo,
  OrderBasisNextMonthlyInfo,
  OrderBasisPastMonthlyInfo,
  OrderBasisRequestInfo,
  OrderInfoForBulkOrderInfo,
} from '@/coreInfrastructureModule/axios/generated/orderGroupManage';
import { ICombinedOrderState } from '../pages/useInventoryStore/type';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { ICombinedOrderWithBusiness } from '../domains/entities/InventoryOrder';
import { ParentBusinessFactory } from '../domains/factories/ParentBusinessFactory';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';

/**
 * まとめ発注のAdapter
 */
export class CombinedOrderAdapter {
  /**
   * リクエストを変換するAdapter
   * 基本的にupsertAndSingleSimulationOrderAdapterに合わせています。
   * @param requestInfo - リクエストする情報
   */
  public static adaptCombinedOrderRequest = (requestInfo: ICombinedOrderState): CreateBulkOrderRequest => {
    const targetDate = DateService.convertIDateToDate(requestInfo.desiredDeliveryDate);
    const adaptedOrderInfoForBulkOrder: Array<OrderInfoForBulkOrderInfo> = requestInfo.confirmedOrdersWithBusinessAndBasis.map(
      (orderWithBusinessAndBasis) => {
        const adaptedOrderBasisInfo: OrderBasisRequestInfo = {
          snapshotLatestInventoryAmount: orderWithBusinessAndBasis.basis.snapshotLatestInventoryAmount,
          snapshotExecutionDate: orderWithBusinessAndBasis.basis.snapshotExecutionDate,
          snapshotPackage: orderWithBusinessAndBasis.basis.snapshotPackage,
          snapshotMoq: orderWithBusinessAndBasis.basis.snapshotMoq,
          userAdjustedLeadTime: orderWithBusinessAndBasis.basis.userAdjustedLeadTime.value,
          userAdjustedLeadTimeUnit: orderWithBusinessAndBasis.basis.userAdjustedLeadTime.unit,
          userAdjustedStdInventoryIndexMax: orderWithBusinessAndBasis.basis.userAdjustedStdInventoryIndex.max,
          userAdjustedStdInventoryIndexMin: orderWithBusinessAndBasis.basis.userAdjustedStdInventoryIndex.min,
          userAdjustedStdInventoryIndexUnit: orderWithBusinessAndBasis.basis.userAdjustedStdInventoryIndex.option.unit,
          nextMonthlyInfos: orderWithBusinessAndBasis.basis.nextMonthlyInfos.map(
            (nextMonthlyInfo): OrderBasisNextMonthlyInfo => ({
              orderBasisTargetYear: nextMonthlyInfo.orderBasisTargetYearMonth.year,
              orderBasisTargetMonth: nextMonthlyInfo.orderBasisTargetYearMonth.month,
              userAdjustedCustomerForecastAmount: nextMonthlyInfo.userAdjustedCustomerForecastAmount.toFixed(6),
              snapshotCustomerForecastAmount: nextMonthlyInfo.snapshotCustomerForecastAmount.toFixed(6),
              snapshotPreInventoryAmount: nextMonthlyInfo.snapshotPreInventoryAmount.toFixed(6),
              dailyInfos: nextMonthlyInfo.dailyInfos.map(
                (nextDailyInfo): OrderBasisNextDailyInfo => ({
                  orderBasisTargetDay: nextDailyInfo.orderBasisTargetDay,
                  snapshotArrivalAmount: nextDailyInfo.snapshotArrivalAmount.toFixed(6),
                })
              ),
            })
          ),
          pastMonthlyInfos: orderWithBusinessAndBasis.basis.pastMonthlyInfos.map(
            (pastMonthlyInfo): OrderBasisPastMonthlyInfo => ({
              orderBasisTargetYear: pastMonthlyInfo.orderBasisTargetYearMonth.year,
              orderBasisTargetMonth: pastMonthlyInfo.orderBasisTargetYearMonth.month,
              snapshotCustomerForecastAmount: pastMonthlyInfo.snapshotCustomerForecastAmount.toFixed(6),
              snapshotSalesResultAmount: pastMonthlyInfo.snapshotSalesResultAmount.toFixed(6),
            })
          ),
        };
        return {
          orderId: orderWithBusinessAndBasis.order.arrivalId,
          aggregateBusinessId: orderWithBusinessAndBasis.business.businessId,
          displayId: orderWithBusinessAndBasis.order.displayId,
          createdOrderDate: undefined,
          desiredDeliveryDate: DateService.formatDateToISOString(targetDate),
          estimatedTimeOfArrival: DateService.formatDateToISOString(targetDate),
          orderAmount: orderWithBusinessAndBasis.order.amount,
          orderStatus: OrderStatus.ApprovedAwaitingDeliveryDate, // 現状作成した取引は全て承認ステータスになる
          orderNote: orderWithBusinessAndBasis.order.memoText,
          updateAt: orderWithBusinessAndBasis.order.updateAt,
          basisInfo: adaptedOrderBasisInfo,
        };
      }
    );
    return {
      orderInfoForBulkOrder: adaptedOrderInfoForBulkOrder,
      orderGroupName: requestInfo.groupName,
      invoiceNumber: requestInfo.invoiceNumber, // TODO: 現状未対応
      upperLimitQuantity: requestInfo.maxTotalAmount,
      destinationName: requestInfo.destinationName, // TODO: 現状未対応
      memo: requestInfo.memo, // TODO: 現状未対応
    };
  };

  /**
   * レスポンスを変換するAdapter
   * @param responseInfo - 帰ってきた情報
   */
  public static adaptCombinedOrderResponse = (responseInfo: CreateBulkOrderResponseDataInner[]): ICombinedOrderWithBusiness[] => {
    return responseInfo.map((info): ICombinedOrderWithBusiness => {
      return {
        business: ParentBusinessFactory.create({
          businessId: info.aggregateBusinessId,
          recommendEstimateTime: info.recommendEstimateTime ? DateService.convertUnixTimeToDate(info.recommendEstimateTime) : undefined,
          recommendArrivalValue: info.recommendArrivalValue ?? undefined,
        }),
        order: {
          displayId: info.displayId,
          orderNumber: '',
          arrivalId: info.orderId,
          crossDate: info.recommendCreatedOrderDate ? DateService.convertUnixTimeToIDate(info.recommendCreatedOrderDate) : undefined,
          fixOrderDate: undefined,
          amount: 0,
          leadTime: 0,
          isFixOrdered: false,
          status: undefined,
          memoText: undefined,
          partialDeliveries: undefined, // 分納の情報はここでは不要
          year: 0,
          month: 0,
          day: 0,
          number: undefined,
          updateAt: undefined,
        },
      };
    });
  };
}
